import React, { useState, useRef, useEffect } from "react";
import { Slider, SliderInput, SliderTrack, SliderRange, SliderHandle } from "@reach/slider";
import "@reach/slider/styles.css";
import ct from "~/common/concat";
import Icon from "../../../../Icons/Standard";
import useFullScreen from "../hooks/useFullScreen";
import { formatTime } from "../../../utils";
import { thumbnailBreaks, useThumbnails } from "../hooks/useThumbnails";
interface ControlsProps {
  alterTime: (e: React.SyntheticEvent, keyType: string, timeAmount?: number) => void;
  toggleVideo: () => void;
  activePlayer: boolean;
  videoRef: React.RefObject<HTMLVideoElement>;
  currentVideoTime: number;
  handleTimeUpdate: (time: number) => void;
  handleVolumeChange: (volume: number) => void;
  currentVolume: number;
  previousVolume: number;
  showControls: boolean;
  thumbnailUrl: string;
  videoWrapper: React.RefObject<HTMLDivElement>;
  bufferedPercentage: number;
  isSmallVideoPlayer: boolean;
  isProductReviewVideo: boolean;
  videoPlayerFullscreen: () => void;
}
function Controls({
  alterTime,
  toggleVideo,
  activePlayer,
  videoRef,
  currentVideoTime,
  handleTimeUpdate,
  handleVolumeChange,
  currentVolume,
  previousVolume,
  thumbnailUrl,
  videoWrapper,
  bufferedPercentage,
  isSmallVideoPlayer,
  isProductReviewVideo,
  videoPlayerFullscreen
}: ControlsProps) {
  const [volumeActive, setVolumeActive] = useState(false);
  const [activeThumbnail, setActiveThumbnail] = useState("");
  const [previousTimeBreak, setPreviousTimeBreak] = useState(0);
  const [showThumbnail, setShowThumnbail] = useState(false);
  const [thumbnailX, setThumbnailX] = useState(0);
  const [hoverTime, setHoverTime] = useState("0:00");
  const [isMobile, setIsMobile] = useState(false);
  const thumbnailUrls = useThumbnails(videoRef, thumbnailUrl);
  const {
    fullScreen
  } = useFullScreen(videoWrapper);
  const thumbnailRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (window.innerWidth < 1368 && navigator.maxTouchPoints > 0) {
        setIsMobile(true);
      } else {
        setIsMobile(false);
      }
    });
    resizeObserver.observe(document.body);
    return function cleanup() {
      resizeObserver.disconnect();
    };
  }, []);
  const handleTrackMouseMove = (e: React.MouseEvent) => {
    if (!Object.keys(thumbnailUrls).length) return;
    const {
      clientX
    } = e;
    const {
      width,
      left
    } = e.currentTarget.getBoundingClientRect();
    if (thumbnailRef.current) {
      const thumbnailWidth = thumbnailRef.current.getBoundingClientRect().width;
      const thumbnailCenter = thumbnailWidth / 2;
      const x = clientX - left;
      const thumbnailXCoordinate = Math.min(Math.max(thumbnailCenter, x), width - thumbnailCenter);
      const percentHover = x / width;
      if (videoRef.current) {
        const time = videoRef.current.duration * percentHover;
        const timeBreak = Math.floor(time / thumbnailBreaks) * thumbnailBreaks;
        setHoverTime(formatTime(time));
        setShowThumnbail(true);
        setThumbnailX(thumbnailXCoordinate);
        if (timeBreak === previousTimeBreak) return;
        const blob = thumbnailUrls[timeBreak];
        const reader = new FileReader();
        reader.addEventListener("load", () => {
          if (typeof reader.result === "string") {
            setActiveThumbnail(reader.result);
          }
          setPreviousTimeBreak(timeBreak);
        });
        if (blob) {
          reader.readAsDataURL(blob);
        }
      }
    }
  };
  const handleTrackMouseLeave = () => {
    setShowThumnbail(false);
  };
  const alterWidth = (e: React.SyntheticEvent) => {
    if (e.type === "mouseenter") {
      setVolumeActive(true);
    } else if (e.type === "mouseleave") {
      setVolumeActive(false);
    }
  };
  return <>
      <div className={`absolute w-full top-1/2 ${isProductReviewVideo ? "translate-y-[calc(-50%-20px)] xl:-translate-y-1/2" : "-translate-y-1/2"}`}>
        <div className="items-center space-between w-full flex">
          <button aria-label="rewind 10 seconds" onClick={e => {
          alterTime(e, "left", 10);
        }} className="flex-1 bg-transparent border-0 cursor-pointer text-white flex-col justify-center items-center transition-opacity ease-in-out duration-150 opacity-50 hover:opacity-100" type="button" style={isSmallVideoPlayer && !fullScreen ? {
          marginBottom: "15%"
        } : undefined}>
            <Icon className={`text-white fill-current -scale-x-1 ${isProductReviewVideo ? "w-10 h-10 xl:w-12 xl:h-12" : "w-14 h-14"}`} name="reload" data-sentry-element="Icon" data-sentry-source-file="Controls.tsx" />
            <span className={ct("block", isSmallVideoPlayer && !fullScreen || isProductReviewVideo ? "text-sm" : "", isProductReviewVideo ? "xl:text-base" : "")}>
              10 seconds
            </span>
          </button>
          <button aria-label={activePlayer ? "pause" : "play"} type="button" data-testid="video-player-play-pause-button" className="bg-transparent px-0 border-0 cursor-pointer flex-1 transition-opacity ease-in-out duration-150 opacity-50 hover:opacity-100" onClick={toggleVideo}>
            <Icon name={activePlayer ? "pause" : "play"} className={`text-white fill-current ${isProductReviewVideo ? "w-14 h-14 xl:w-16 xl:h-16" : "w-20 h-20"}`} style={isSmallVideoPlayer && !fullScreen ? {
            marginBottom: "50%"
          } : undefined} key={activePlayer.toString()} data-sentry-element="Icon" data-sentry-source-file="Controls.tsx" />
          </button>
          <button aria-label="fast forward 10 seconds" onClick={e => {
          alterTime(e, "right", 10);
        }} className="bg-transparent border-0 cursor-pointer text-white flex-col justify-center flex-1 items-center transition-opacity ease-in-out duration-150 opacity-50 hover:opacity-100" type="button" style={isSmallVideoPlayer && !fullScreen ? {
          marginBottom: "15%"
        } : undefined}>
            <Icon className={`text-white fill-current ${isProductReviewVideo ? "w-10 h-10 xl:w-12 xl:h-12" : "w-14 h-14"}`} name="reload" data-sentry-element="Icon" data-sentry-source-file="Controls.tsx" />
            <span className={ct("block", isSmallVideoPlayer && !fullScreen || isProductReviewVideo ? "text-sm" : "", isProductReviewVideo ? "xl:text-base" : "")}>
              10 seconds
            </span>
          </button>
        </div>
      </div>
      <div className="absolute bottom-0 w-full" style={{
      background: "linear-gradient(180deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.7))"
    }}>
        <div className={ct("px-2", "pb-1", isSmallVideoPlayer && !fullScreen ? "" : "pt-1")}>
          <div onMouseMove={handleTrackMouseMove} onMouseLeave={handleTrackMouseLeave} className="relative">
            <div className={`${showThumbnail ? "block" : "hidden"} absolute text-center border border-solid border-2 border-black`} ref={thumbnailRef} style={{
            left: `${thumbnailX}px`,
            bottom: 25,
            transform: "translateX(-50%)",
            width: 180
          }}>
              <img src={activeThumbnail} alt="" />
              <div className="bg-black py-1/2">
                <p className="text-gray-50 text-center text-base m-0">
                  {hoverTime}
                </p>
              </div>
            </div>
            {videoRef.current && <>
                <div className="bg-opacity-75 bg-white w-full h-2 absolute rounded-sm" style={{
              width: `${bufferedPercentage}%`,
              top: 8
            }} />
                <SliderInput min={0} max={videoRef.current.duration || 1} step={0.25} value={currentVideoTime || 0} onChange={handleTimeUpdate} data-id="playback">
                  <SliderTrack className="rounded-none" data-id="playback-track">
                    <SliderRange style={{
                  background: "#549b33"
                }} data-id="playback-highlight" />
                    <SliderHandle data-id="playback-handle" />
                  </SliderTrack>
                </SliderInput>
              </>}
          </div>

          <div className={ct("flex", "items-center", (isSmallVideoPlayer || isProductReviewVideo) && !fullScreen ? "" : "mt-2")} style={isSmallVideoPlayer && !fullScreen ? {
          marginTop: "0px"
        } : undefined}>
            <div>
              <button aria-label={activePlayer ? "pause" : "play"} type="button" className="bg-transparent border-0 cursor-pointer leading-none" onClick={toggleVideo}>
                <Icon name={activePlayer ? "pause" : "play"} className="w-6 h-6 text-white fill-current transition-opacity ease-in-out duration-150 opacity-75 hover:opacity-100" key={activePlayer.toString()} data-sentry-element="Icon" data-sentry-source-file="Controls.tsx" />
              </button>
            </div>
            <div className="flex items-center mx-2 transition-all h-full duration-200 ease-in-out mr-auto" onMouseEnter={alterWidth} onMouseLeave={alterWidth} style={{
            width: `${volumeActive ? "w-16" : "w-0"}`
          }}>
              <button aria-label={currentVolume > 0 ? "mute volume" : "unmute volume"} type="button" className="cursor-pointer bg-transparent border-0 p-0" onClick={() => {
              handleVolumeChange(currentVolume > 0 ? 0 : previousVolume);
            }}>
                <Icon data-testid="video-player-volume-icon" name={currentVolume > 0 ? "volume" : "volume-mute"} className="w-6 h-6 text-white fill-current transition-opacity ease-in-out duration-150 opacity-75 hover:opacity-100" key={currentVolume} data-sentry-element="Icon" data-sentry-source-file="Controls.tsx" />
              </button>
              {!isMobile ? <div className={`${volumeActive ? "w-16  pl-4 pr-3" : "w-0"} overflow-hidden transition-all duration-200`}>
                  <Slider onChange={handleVolumeChange} min={0} max={100} step={1} value={currentVolume} handleAlignment="center" />
                </div> : null}
            </div>
            {videoRef.current && videoRef.current.readyState > 0 && <div className={ct("text-sm", "ml-3", "text-white", "w-full", isSmallVideoPlayer && !fullScreen ? "text-xs" : "")}>
                {formatTime(currentVideoTime)}
                {" / "}
                {formatTime(videoRef.current.duration)}
              </div>}
            <div>
              <button aria-label={fullScreen ? "disable full screen" : "enable full screen"} onClick={videoPlayerFullscreen} type="button" className="bg-transparent border-0 cursor-pointer">
                <Icon name="expand" className="text-white h-4 w-4 fill-current transition-opacity ease-in-out duration-150 opacity-75 hover:opacity-100" data-sentry-element="Icon" data-sentry-source-file="Controls.tsx" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>;
}
export default Controls;